import React, { useState, useRef, useEffect, useCallback } from "react";
import L from "leaflet";
import { toast } from "sonner";
import {
  MapPin, Loader2, Pill, HeartPulse, Landmark, ShoppingCart, Bus,
  GraduationCap, Home, ExternalLink, AlertCircle, Building2,
  RefreshCw, Pencil, Navigation, Trash2, Plus, Star, X, Utensils, Info,
} from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { UI, t } from "@/i18n/translations";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

const CAT_META = {
  pharmacy: { Icon: Pill, color: "#3E8E72" },
  health: { Icon: HeartPulse, color: "#C4553D" },
  supermarket: { Icon: ShoppingCart, color: "#E8A33D" },
  bank: { Icon: Landmark, color: "#22314F" },
  transport: { Icon: Bus, color: "#3E8E72" },
  crous: { Icon: Utensils, color: "#C4553D" },
  prefecture: { Icon: Building2, color: "#22314F" },
  custom: { Icon: Star, color: "#E8A33D" },
};
const CAT_ORDER = ["pharmacy", "health", "supermarket", "bank", "transport", "crous", "prefecture", "custom"];

function pin(color) {
  return L.divIcon({
    className: "",
    html: `<div style="background:${color};width:16px;height:16px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #FAF9F6;box-shadow:0 1px 4px rgba(34,49,79,.4)"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 16],
  });
}

function directionsUrl(place) {
  const q =
    place.lat != null && place.lon != null
      ? `${place.lat},${place.lon}`
      : encodeURIComponent(place.address || place.name || "");
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
}

export default function Reperes() {
  const { lang } = useLang();
  const { user, setUser } = useAuth();
  const R = UI.reperes;

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null); // {home, school, places, official_links}
  const [showForm, setShowForm] = useState(false);
  const [residence, setResidence] = useState({ address: "", lat: null, lon: null, verified: false });
  const [ecole, setEcole] = useState({ address: "", lat: null, lon: null, verified: false });
  const [showAdd, setShowAdd] = useState(false);
  const [newPlace, setNewPlace] = useState({ name: "", addr: { address: "", lat: null, lon: null, verified: false } });
  const [anchor, setAnchor] = useState("residence");

  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  const hasReperes = data && data.places && data.places.length > 0;

  // Initial load of saved reperes
  useEffect(() => {
    api.get("/reperes")
      .then(({ data }) => {
        setData(data);
        setResidence({ address: data.adresse_residence || "", lat: data.home?.lat ?? null, lon: data.home?.lon ?? null, verified: !!data.home });
        setEcole({ address: data.adresse_ecole || "", lat: data.school?.lat ?? null, lon: data.school?.lon ?? null, verified: !!data.school });
        if (!data.has_reperes) setShowForm(true);
      })
      .catch(() => setShowForm(true))
      .finally(() => setLoading(false));
  }, []);

  // Draw / redraw map — recenters on the active anchor (home or school)
  useEffect(() => {
    if (!hasReperes || !mapEl.current || !data.home) return;
    const origin = anchor === "school" ? data.school : data.home;
    if (!origin) return;
    if (!mapRef.current) {
      mapRef.current = L.map(mapEl.current, { scrollWheelZoom: false });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap", maxZoom: 19,
      }).addTo(mapRef.current);
    }
    if (layerRef.current) layerRef.current.remove();
    layerRef.current = L.layerGroup().addTo(mapRef.current);

    mapRef.current.setView([origin.lat, origin.lon], 14);
    const originColor = anchor === "school" ? "#E8A33D" : "#22314F";
    const originLabel = anchor === "school" ? t(R.yourSchool, lang) : t(R.yourHome, lang);
    L.marker([origin.lat, origin.lon], { icon: pin(originColor) })
      .bindPopup(`<b>${originLabel}</b><br>${origin.label}`)
      .addTo(layerRef.current);
    (data.places || []).filter((p) => (p.anchor || "residence") === anchor).forEach((p) => {
      if (p.lat == null || p.lon == null) return;
      const color = CAT_META[p.category]?.color || "#3E8E72";
      L.marker([p.lat, p.lon], { icon: pin(color) })
        .bindPopup(`<b>${p.name || t(R.categories[p.category], lang)}</b>${p.address ? "<br>" + p.address : ""}`)
        .addTo(layerRef.current);
    });
    setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 200);
  }, [data, hasReperes, lang, R, anchor]);

  const runSearch = useCallback(async (res, eco) => {
    if (!res?.address?.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/reperes/search", {
        adresse_residence: res.address,
        adresse_ecole: eco?.address || null,
        residence_lat: res.lat,
        residence_lon: res.lon,
        ecole_lat: eco?.lat,
        ecole_lon: eco?.lon,
      });
      setData((prev) => ({ ...data, adresse_residence: res.address, adresse_ecole: eco?.address }));
      setShowForm(false);
      if (user) setUser({ ...user, adresse_residence: res.address, adresse_ecole: eco?.address });
      if (!data.overpass_ok) toast.warning(t(R.overpassError, lang));
    } catch (e) {
      if (e.response?.status === 422) toast.error(t(R.geocodeError, lang));
      else toast.error(formatApiErrorDetail(e.response?.data?.detail) || UI.common.error[lang]);
    } finally {
      setBusy(false);
    }
  }, [lang, R, user, setUser]);

  const removePlace = async (id) => {
    try {
      const { data: fresh } = await api.delete(`/reperes/place/${id}`);
      setData((prev) => ({ ...prev, places: fresh.places }));
      toast.success(t(R.removed, lang));
    } catch {
      toast.error(UI.common.error[lang]);
    }
  };

  const addPlace = async () => {
    if (!newPlace.addr.address.trim()) return;
    setBusy(true);
    try {
      const { data: place } = await api.post("/reperes/place", {
        name: newPlace.name,
        address: newPlace.addr.address,
        lat: newPlace.addr.lat,
        lon: newPlace.addr.lon,
        anchor,
      });
      setData((prev) => ({ ...prev, places: [...(prev.places || []), place] }));
      setNewPlace({ name: "", addr: { address: "", lat: null, lon: null, verified: false } });
      setShowAdd(false);
      toast.success(t(R.added, lang));
    } catch (e) {
      if (e.response?.status === 422) toast.error(t(R.geocodeError, lang));
      else toast.error(UI.common.error[lang]);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin text-amber" /></div>;
  }

  const anchorPlaces = (data?.places || []).filter((p) => (p.anchor || "residence") === anchor);
  const grouped = CAT_ORDER
    .map((cat) => ({ cat, places: anchorPlaces.filter((p) => p.category === cat) }))
    .filter((g) => g.places.length > 0);
  const schoolMissing = !data?.school;
  const anchorExpanded = anchor === "school" ? data?.school_expanded : data?.home_expanded;

  return (
    <div className="animate-fade-up space-y-5">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">
          {hasReperes && !showForm ? t(R.myReperes, lang) : t(R.title, lang)}
        </h1>
        <p className="text-sm text-ink/60 mt-1">
          {hasReperes && !showForm ? t(R.savedIntro, lang) : t(R.intro, lang)}
        </p>
      </div>

      {/* Address form (first search or edit) */}
      {(showForm || !hasReperes) && (
        <div className="rounded-[18px] bg-white border border-line p-5 space-y-3 shadow-sm">
          <label className="block">
            <span className="block text-sm font-semibold mb-1.5">{t(R.residence, lang)} *</span>
            <AddressAutocomplete
              testId="residence-input"
              value={residence}
              onChange={setResidence}
              placeholder={t(R.residencePh, lang)}
              iconColor="#E8A33D"
            />
          </label>
          <label className="block">
            <span className="block text-sm font-semibold mb-1.5">{t(R.ecole, lang)}</span>
            <AddressAutocomplete
              testId="ecole-input"
              value={ecole}
              onChange={setEcole}
              placeholder={t(R.ecole, lang)}
              iconColor="#3E8E72"
            />
          </label>
          <div className="flex gap-2">
            <button
              data-testid="find-reperes-btn"
              onClick={() => runSearch(residence, ecole)}
              disabled={busy || !residence.address.trim()}
              className="flex-1 rounded-2xl bg-amber text-ink font-bold py-3.5 flex items-center justify-center gap-2 hover:brightness-105 active:scale-[0.99] transition disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
              {busy ? t(R.searching, lang) : t(R.find, lang)}
            </button>
            {hasReperes && (
              <button
                onClick={() => setShowForm(false)}
                className="rounded-2xl border border-line bg-white px-4 font-semibold text-sm text-ink/70"
              >
                {t(R.cancel, lang)}
              </button>
            )}
          </div>
        </div>
      )}

      {hasReperes && !showForm && (
        <>
          {/* Action buttons */}
          <div className="flex gap-2">
            <button
              data-testid="refresh-reperes-btn"
              onClick={() => runSearch(residence, ecole)}
              disabled={busy}
              className="flex-1 rounded-2xl bg-white border border-line py-3 text-sm font-semibold flex items-center justify-center gap-2 hover:border-amber transition-colors disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 text-sage" />}
              {t(R.refresh, lang)}
            </button>
            <button
              data-testid="edit-addresses-btn"
              onClick={() => setShowForm(true)}
              className="flex-1 rounded-2xl bg-white border border-line py-3 text-sm font-semibold flex items-center justify-center gap-2 hover:border-amber transition-colors"
            >
              <Pencil className="w-4 h-4 text-amber" />
              {t(R.editAddresses, lang)}
            </button>
          </div>

          {/* Anchor selector (Residence vs School) */}
          <div className="grid grid-cols-2 gap-1 rounded-2xl bg-ink/5 p-1" data-testid="anchor-selector">
            {[
              { key: "residence", label: t(R.tabResidence, lang), Icon: Home },
              { key: "school", label: t(R.tabSchool, lang), Icon: GraduationCap },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                data-testid={`anchor-tab-${key}`}
                onClick={() => setAnchor(key)}
                className={`flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold transition-colors ${anchor === key ? "bg-white shadow-sm text-ink" : "text-ink/55"}`}
              >
                <Icon className="w-4 h-4" strokeWidth={2.2} /> {label}
              </button>
            ))}
          </div>

          {anchor === "school" && schoolMissing ? (
            /* Warm empty state — no school address yet */
            <div data-testid="school-empty-state" className="rounded-[18px] bg-white border border-line p-6 text-center shadow-sm">
              <span className="mx-auto grid place-items-center w-12 h-12 rounded-2xl bg-amber/15 mb-3">
                <GraduationCap className="w-6 h-6 text-amber" strokeWidth={2.2} />
              </span>
              <h3 className="font-display text-lg font-bold mb-1">{t(R.schoolEmptyTitle, lang)}</h3>
              <p className="text-sm text-ink/60 mb-4">{t(R.schoolEmptyBody, lang)}</p>
              <button
                data-testid="add-school-address-btn"
                onClick={() => setShowForm(true)}
                className="rounded-2xl bg-amber text-ink font-bold px-5 py-3 inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> {t(R.schoolEmptyBtn, lang)}
              </button>
            </div>
          ) : (
          <>
          {/* Map */}
          <div ref={mapEl} data-testid="reperes-map" className="h-64 w-full rounded-[18px] border border-line overflow-hidden shadow-sm z-0" />

          {/* Active origin row */}
          <div className="space-y-2">
            {anchor === "residence" && data.home && <RouteRow Icon={Home} color="#22314F" title={t(R.yourHome, lang)} subtitle={data.home.label} href={directionsUrl(data.home)} directionsLabel={t(R.directions, lang)} />}
            {anchor === "school" && data.school && <RouteRow Icon={GraduationCap} color="#E8A33D" title={t(R.yourSchool, lang)} subtitle={data.school.label} href={directionsUrl(data.school)} directionsLabel={t(R.directions, lang)} />}
          </div>

          {/* Expanded-radius notice */}
          {anchorExpanded && (
            <p data-testid="expanded-notice" className="flex items-start gap-2 text-xs text-ink/70 leading-relaxed rounded-2xl bg-amber/10 border border-amber/30 px-4 py-3">
              <Info className="w-4 h-4 shrink-0 mt-0.5 text-amber" /> {t(R.expandedNotice, lang)}
            </p>
          )}

          {/* Add place */}
          {showAdd ? (
            <div className="rounded-[18px] bg-white border border-line p-5 space-y-3 shadow-sm" data-testid="add-place-form">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-base font-bold">{t(R.addTitle, lang)}</h3>
                <button onClick={() => setShowAdd(false)} className="text-ink/40"><X className="w-5 h-5" /></button>
              </div>
              <input
                data-testid="place-name-input"
                value={newPlace.name}
                maxLength={80}
                onChange={(e) => setNewPlace((p) => ({ ...p, name: e.target.value }))}
                placeholder={t(R.placeName, lang)}
                className="w-full rounded-2xl border border-line bg-paper px-3.5 py-3 text-sm outline-none focus:border-amber"
              />
              <AddressAutocomplete
                testId="place-address-input"
                value={newPlace.addr}
                onChange={(v) => setNewPlace((p) => ({ ...p, addr: v }))}
                placeholder={t(R.placeAddress, lang)}
                iconColor="#E8A33D"
              />
              <button
                data-testid="add-place-submit"
                onClick={addPlace}
                disabled={busy || !newPlace.addr.address.trim()}
                className="w-full rounded-2xl bg-amber text-ink font-bold py-3 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {t(R.addBtn, lang)}
              </button>
            </div>
          ) : (
            <button
              data-testid="show-add-place-btn"
              onClick={() => setShowAdd(true)}
              className="w-full rounded-2xl border border-dashed border-line bg-white/60 py-3 text-sm font-semibold text-ink/70 flex items-center justify-center gap-2 hover:border-amber transition-colors"
            >
              <Plus className="w-4 h-4 text-amber" /> {t(R.addTitle, lang)}
            </button>
          )}

          {/* Grouped places */}
          {grouped.map(({ cat, places }) => {
            const meta = CAT_META[cat];
            return (
              <div key={cat} data-testid={`cat-${cat}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="grid place-items-center w-7 h-7 rounded-lg" style={{ background: `${meta.color}22` }}>
                    <meta.Icon className="w-4 h-4" style={{ color: meta.color }} strokeWidth={2.2} />
                  </span>
                  <h3 className="font-display text-lg font-bold">{t(R.categories[cat], lang)}</h3>
                  <span className="text-xs text-ink/40 font-semibold">{places.length}</span>
                </div>
                <div className="space-y-1.5">
                  {places.map((p) => (
                    <div key={p.id} data-testid={`place-${p.id}`} className="rounded-2xl bg-white border border-line px-4 py-3 flex items-center gap-3">
                      <a
                        href={directionsUrl(p)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`directions-${p.id}`}
                        className="flex-1 min-w-0 group"
                      >
                        <div className="font-semibold text-sm group-hover:text-sage transition-colors">{p.name || t(R.categories[cat], lang)}</div>
                        {p.address && <div className="text-xs text-ink/55 mt-0.5">{p.address}</div>}
                        {(p.outside || p.distance_km != null) && (
                          <div className="flex flex-wrap items-center gap-1.5 mt-1">
                            {p.outside && p.commune && (
                              <span data-testid={`nearby-badge-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber/15 text-amber text-[11px] font-semibold px-2 py-0.5">
                                <MapPin className="w-3 h-3" /> {t(R.nearbyCommune, lang)} : {p.commune}
                              </span>
                            )}
                            {p.distance_km != null && (
                              <span className="text-[11px] font-medium text-ink/45">{t(R.awayApprox, lang)}{p.distance_km.toFixed(1)} km</span>
                            )}
                          </div>
                        )}
                      </a>
                      <a
                        href={directionsUrl(p)}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={t(R.directions, lang)}
                        className="shrink-0 grid place-items-center w-10 h-10 rounded-xl bg-sage/12 text-sage hover:bg-sage hover:text-white transition-colors"
                      >
                        <Navigation className="w-4.5 h-4.5" strokeWidth={2.2} />
                      </a>
                      <button
                        data-testid={`remove-${p.id}`}
                        onClick={() => removePlace(p.id)}
                        aria-label={t(R.remove, lang)}
                        className="shrink-0 grid place-items-center w-10 h-10 rounded-xl text-ink/35 hover:text-coral hover:bg-coral/8 transition-colors"
                      >
                        <Trash2 className="w-4.5 h-4.5" strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          </>
          )}

          {/* Official tools */}
          <div className="rounded-[18px] bg-ink/5 border border-line p-4">
            <p className="text-sm font-semibold flex items-center gap-2 mb-3">
              <Building2 className="w-4 h-4 text-ink/60" /> {t(R.officialNote, lang)}
            </p>
            <div className="space-y-2">
              <OfficialLink label={t(R.prefecture, lang)} href={data.official_links.prefecture} cta={t(R.openTool, lang)} />
              <OfficialLink label={t(R.crous, lang)} href={data.official_links.crous} cta={t(R.openTool, lang)} />
            </div>
          </div>

          <p className="flex items-start gap-2 text-xs text-ink/50 leading-relaxed">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber" />
            {t(R.disclaimer, lang)}
          </p>
        </>
      )}
    </div>
  );
}

function RouteRow({ Icon, color, title, subtitle, href, directionsLabel }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-white border border-line px-4 py-3">
      <span className="grid place-items-center w-9 h-9 rounded-xl shrink-0" style={{ background: `${color}18` }}>
        <Icon className="w-5 h-5" style={{ color }} strokeWidth={2.2} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-sm">{title}</div>
        <div className="text-xs text-ink/55 truncate">{subtitle}</div>
      </div>
      <a href={href} target="_blank" rel="noreferrer" aria-label={directionsLabel} className="shrink-0 grid place-items-center w-10 h-10 rounded-xl bg-sage/12 text-sage hover:bg-sage hover:text-white transition-colors">
        <Navigation className="w-4.5 h-4.5" strokeWidth={2.2} />
      </a>
    </div>
  );
}

function OfficialLink({ label, href, cta }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="flex items-center justify-between rounded-2xl bg-white border border-line px-4 py-3 hover:border-amber transition-colors">
      <span className="font-semibold text-sm">{label}</span>
      <span className="flex items-center gap-1.5 text-xs font-semibold text-sage">{cta} <ExternalLink className="w-3.5 h-3.5" /></span>
    </a>
  );
}
